/**
 * Human-readable labels for the persisted absorption `source_kind` (ADR-046),
 * mirrored from the backend's single registry
 * (`backend/app/services/absorption/source_kinds.py`) so the source-list
 * folders (ADR-049) show the same clinician-facing names the backend already
 * uses — never a raw kind string.
 *
 * A manually uploaded source has no `source_kind` (it isn't absorbed from the
 * warehouse) — `UNTYPED_SOURCE_KIND` is a client-only sentinel grouping key
 * for that bucket, never sent to or read from the backend.
 */

/** Client-only sentinel grouping key for a source with no persisted kind. */
export const UNTYPED_SOURCE_KIND = "uploaded";

/** Sentinel folder grouping key for all clinical assessments. */
export const ASSESSMENTS_FOLDER_KEY = "assessments";

/** Known clinical instrument / assessment kinds absorbed from datamarts. */
export const ASSESSMENT_KINDS = [
  "phq9",
  "gad7",
  "vanderbilt",
  "basc3",
] as const;

/** Check whether a source kind represents a clinical assessment instrument. */
export function isAssessmentKind(kind: string | null | undefined): boolean {
  if (!kind) return false;
  const normalized = kind.toLowerCase();
  return (
    (ASSESSMENT_KINDS as readonly string[]).includes(normalized) ||
    normalized.startsWith("assessment_")
  );
}

const SOURCE_KIND_LABELS: Record<string, string> = {
  [ASSESSMENTS_FOLDER_KEY]: "Assessments",
  phq9: "PHQ-9",
  gad7: "GAD-7",
  vanderbilt: "Vanderbilt Assessment",
  basc3: "BASC-3 Caregiver Rating Scale",
  visit_note_npe: "New Patient Evaluation",
  visit_note_slp_eval: "Speech & Language Evaluation",
  visit_note_ot_eval: "Occupational Therapy Evaluation",
  visit_note_initial_therapy: "Initial Therapy Visit",
  visit_note_med_mgmt: "Medication Management Visit",
  visit_note_psychotherapy: "Psychotherapy Visit",
  visit_note_autism_eval: "Autism Evaluation Visit",
  visit_note_medical_counseling: "Medical Counseling Visit",
  visit_note_caregiver_support: "Caregiver Support Session",
  visit_note_diagnostic_review: "Diagnostic Review",
  visit_note_re_evaluation: "Re-Evaluation",
  unknown: "Other",
  [UNTYPED_SOURCE_KIND]: "Uploaded",
};

/**
 * Turn a snake_case kind token into a human "Sentence case" label.
 *
 * A last-resort fallback for a kind this map doesn't (yet) know about — never
 * surfaces the raw enum string to a clinician.
 *
 * @param value - The raw kind token.
 * @returns A humanised label.
 */
function humanizeToken(value: string): string {
  const words = value.replace(/[-_]+/g, " ").trim();
  if (words === "") return value;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Human-readable label for a persisted (or absent) `source_kind`.
 *
 * @param kind - The document's `source_kind`, or `null`/`undefined` for a
 *   manually uploaded source with no persisted kind.
 * @returns The label from {@link SOURCE_KIND_LABELS}, or a humanised
 *   fallback for an unrecognised non-empty kind.
 */
export function sourceKindLabel(kind: string | null | undefined): string {
  if (kind === null || kind === undefined || kind === "") {
    return SOURCE_KIND_LABELS[UNTYPED_SOURCE_KIND];
  }
  return SOURCE_KIND_LABELS[kind] ?? humanizeToken(kind);
}

/** Folder-header icon per kind family — purely decorative, never load-bearing. */
export function sourceKindIcon(kind: string | null | undefined): string {
  if (kind === null || kind === undefined || kind === "") return "⬆️";
  if (kind === ASSESSMENTS_FOLDER_KEY || isAssessmentKind(kind)) {
    return "📊";
  }
  if (kind.startsWith("visit_note")) return "🗂️";
  return "🗂️";
}
